import React from 'react';
const { useState, useEffect } = React;
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, badgeSt } from '../../diagnostic/shared.jsx';
import { PHOTO_KINDS, PHOTO_AUTHORIZATIONS, BRAND_TEXT_LIMITS as BL, photoUsable } from '../../../brandConfig.js';
import { ownerCall, Notice } from '../shared.jsx';
import { EmptyState } from '../../ui/States.jsx';
import { preparePhoto, loadPhoto, forgetPhoto } from './brandClient.js';

// Photos of Ale and her guests, with the person's name, role and the
// authorization to use the photo. Photos are never retouched, filtered or
// generated: pieces only crop them and choose the framing. A guest photo
// without authorization can be placed in a draft, but never approved or exported.

const label = (list, k) => list.find(x => x.key === k)?.label || k;
export const photoStatus = (p) => {
  const u = photoUsable(p);
  if (u.ok) return { ok: true, text: p.kind === 'self' ? 'Propia' : 'Autorizada' };
  return { ok: false, text: { not_authorized: label(PHOTO_AUTHORIZATIONS, p.authorization), no_name: 'Falta el nombre', no_credit: 'Falta el crédito', missing: 'Archivada' }[u.reason] || 'Revisar' };
};

export function Thumb({ photo, size = 72, alt }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let on = true;
    if (photo?.hasImage) loadPhoto(photo.id).then(x => on && setSrc(x.src)).catch(() => on && setFailed(true));
    return () => { on = false; };
  }, [photo?.id, photo?.hasImage]);
  const st = { width: size, height: size, borderRadius: 8, objectFit: 'cover', background: 'rgba(30,42,44,0.06)', display: 'block', flex: '0 0 auto' };
  if (!src) return <span role="img" aria-label={failed ? 'No se pudo cargar la foto' : 'Cargando foto'} style={{ ...st, display: 'grid', placeItems: 'center', fontSize: 11, color: 'var(--globant-muted)' }}>{failed ? '—' : '…'}</span>;
  return <img src={src} alt={alt ?? (photo.personName || photo.name || 'Foto')} style={st} />;
}

function MetaForm({ value, onChange }) {
  const v = value;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 180px' }}><span style={labelSt}>De quién es</span>
          <select style={inputSt} value={v.kind} onChange={e => onChange({ ...v, kind: e.target.value, authorization: e.target.value === 'self' ? 'own' : (v.authorization === 'own' ? 'pending' : v.authorization) })}>
            {PHOTO_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select></label>
        <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Nombre de la foto</span><input style={inputSt} value={v.name} maxLength={BL.photoName} onChange={set('name')} placeholder="Retrato en el balcón" /></label>
      </div>
      {v.kind !== 'self' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Nombre de la persona{v.kind === 'guest' ? ' *' : ''}</span><input style={inputSt} value={v.personName} maxLength={BL.personName} onChange={set('personName')} /></label>
            <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Rol</span><input style={inputSt} value={v.personRole} maxLength={BL.personRole} onChange={set('personRole')} placeholder="Psicóloga, periodista…" /></label>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Autorización de uso *</span>
              <select style={inputSt} value={v.authorization} onChange={set('authorization')}>
                {PHOTO_AUTHORIZATIONS.filter(a => a.key !== 'own').map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select></label>
            <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Crédito (fotógrafo/a)</span><input style={inputSt} value={v.credit} maxLength={BL.credit} onChange={set('credit')} /></label>
          </div>
          <label><span style={labelSt}>Cómo y cuándo se autorizó</span><textarea style={textareaSt} value={v.authorizationNote} maxLength={BL.authorizationNote} onChange={set('authorizationNote')} placeholder="Aceptó por correo el 20 de octubre, para redes y newsletter." /></label>
        </>
      )}
      {v.kind === 'self' && <label><span style={labelSt}>Crédito (opcional)</span><input style={inputSt} value={v.credit} maxLength={BL.credit} onChange={set('credit')} /></label>}
    </div>
  );
}

const emptyMeta = { kind: 'self', name: '', personName: '', personRole: '', credit: '', authorization: 'own', authorizationNote: '' };

/** Upload one photo (scaled down in the browser; nothing else is changed). */
export function PhotoUpload({ onUploaded, defaultKind = 'self', compact = false }) {
  const [file, setFile] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [meta, setMeta] = useState({ ...emptyMeta, kind: defaultKind, authorization: defaultKind === 'self' ? 'own' : 'pending' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pick = async (f) => {
    setError(''); setPrepared(null); setFile(f || null);
    if (!f) return;
    try { setPrepared(await preparePhoto(f)); if (!meta.name) setMeta(m => ({ ...m, name: f.name.replace(/\.[^.]+$/, '').slice(0, BL.photoName) })); } catch (e) { setError(e.message); }
  };
  const upload = async () => {
    setBusy(true); setError('');
    try {
      const { photo } = await ownerCall({ action: 'brandUploadPhoto', photo: meta, contentType: prepared.contentType, data: prepared.data, width: prepared.width, height: prepared.height });
      setFile(null); setPrepared(null); setMeta({ ...emptyMeta, kind: defaultKind, authorization: defaultKind === 'self' ? 'own' : 'pending' });
      onUploaded?.(photo);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ ...cardSt, ...(compact ? { padding: 12 } : {}) }}>
      <b style={{ fontSize: 14 }}>Agregar una foto</b>
      <div style={{ ...mutedSt, margin: '4px 0 8px' }}>JPG, PNG o WebP. Se reduce a 2000 px como máximo, sin deformarla ni retocarla.</div>
      <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Elegir foto" onChange={e => pick(e.target.files?.[0])} />
      {prepared && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 10 }}>
          <img src={prepared.preview} alt="Foto elegida" style={{ width: 120, height: 120, objectFit: 'contain', background: 'rgba(30,42,44,0.06)', borderRadius: 8 }} />
          <div style={{ flex: '1 1 260px' }}>
            <div style={mutedSt}>{prepared.width} × {prepared.height} px{file ? ` · ${file.name}` : ''}</div>
            <MetaForm value={meta} onChange={setMeta} />
          </div>
        </div>
      )}
      {error && <div role="alert" style={{ color: '#B4362C', fontSize: 13, marginTop: 8 }}>{error}</div>}
      {prepared && <div style={{ marginTop: 10 }}><button className="action-btn btn-primary" disabled={busy} onClick={upload}>{busy ? 'Subiendo…' : 'Guardar foto'}</button></div>}
    </div>
  );
}

export default function BrandPhotos({ photos, onChange }) {
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const save = async () => {
    setError('');
    try { await ownerCall({ action: 'brandSavePhoto', id: editing.id, photo: editing }); setEditing(null); onChange(); } catch (e) { setError(e.message); }
  };
  const archive = async (p, archived) => {
    setError('');
    try { await ownerCall({ action: 'brandArchivePhoto', id: p.id, archived }); forgetPhoto(p.id); onChange(); } catch (e) { setError(e.message); }
  };
  const shown = photos.filter(p => showArchived || !p.archived);
  return (
    <div>
      <Notice icon="📷" tone="private">
        Fotos tuyas y de tus invitados. Solo se recortan y se encuadran en cada pieza: nunca se retocan rostros ni se generan fotos. La foto de otra persona necesita su nombre y su autorización para aprobar o exportar una pieza.
      </Notice>
      <PhotoUpload onUploaded={() => onChange()} />
      {error && <div role="alert" style={{ color: '#B4362C', fontSize: 13, margin: '8px 0' }}>{error}</div>}
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, margin: '8px 0' }}><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Mostrar archivadas</label>
      {!shown.length ? <EmptyState title="Todavía no hay fotos">Agrega tu primera foto. Mientras tanto, las piezas funcionan con composición tipográfica o con un espacio reservado.</EmptyState> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {shown.map(p => {
            const st = photoStatus(p);
            return (
              <div key={p.id} style={{ ...cardSt, marginBottom: 0, padding: 12, opacity: p.archived ? 0.6 : 1 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Thumb photo={p} size={84} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, overflowWrap: 'anywhere' }}>{p.kind === 'self' ? (p.name || 'Foto mía') : p.personName || p.name}</div>
                    {p.personRole && <div style={mutedSt}>{p.personRole}</div>}
                    <div style={{ margin: '4px 0' }}><span style={badgeSt(st.ok)}>{label(PHOTO_KINDS, p.kind)}</span> <span style={{ ...badgeSt(st.ok), ...(st.ok ? {} : { color: '#9A6212' }) }}>{st.text}</span></div>
                    {p.width && <div style={mutedSt}>{p.width} × {p.height} px</div>}
                  </div>
                </div>
                {editing?.id === p.id ? (
                  <div style={{ marginTop: 10 }}>
                    <MetaForm value={editing} onChange={setEditing} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button className="action-btn btn-primary" onClick={save}>Guardar</button>
                      <button className="action-btn" onClick={() => setEditing(null)}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <button className="action-btn" onClick={() => setEditing({ ...emptyMeta, ...p })}>Editar datos</button>
                    <button className="action-btn" onClick={() => archive(p, !p.archived)}>{p.archived ? 'Recuperar' : 'Archivar'}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
